import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { appointmentsApi } from "../api/appointments";
import { patientsApi } from "../api/patients";
import { useDebounce } from "../hooks/useDebounce";

/**
 * Global search in the topbar.
 *
 * Before this it was a plain form: typing and pressing Enter navigated to
 * /patients?q=… — but the patient list never read that parameter, so the query
 * was silently dropped and the user had to type it a second time on the page
 * they had just been sent to.
 *
 * Now it resolves in place: results appear under the box as you type, grouped
 * by kind, and picking one jumps straight to that record. Nothing navigates
 * until a result is chosen.
 *
 * Adding a source later (invoice, prescription, doctor…) means adding one entry
 * to `useSources` — the dropdown, keyboard handling and empty states are
 * generic over the group list.
 */

const MIN_CHARS = 2;
const PER_GROUP = 5;

type Hit = {
  id: string;
  title: string;
  subtitle: string;
  to: string;
};

type Group = {
  key: string;
  label: string;
  hits: Hit[];
  loading: boolean;
};

function useSources(term: string, enabled: boolean): Group[] {
  const patients = useQuery({
    queryKey: ["global-search", "patients", term],
    queryFn: () => patientsApi.search(term, { page: 0, size: PER_GROUP }),
    enabled,
  });

  const appointments = useQuery({
    queryKey: ["global-search", "appointments", term],
    queryFn: () => appointmentsApi.list({ q: term }, { page: 0, size: PER_GROUP }),
    enabled,
  });

  return useMemo(
    () => [
      {
        key: "patients",
        label: "Bệnh nhân",
        loading: patients.isLoading,
        hits: (patients.data?.content ?? []).map((p) => ({
          id: p.id,
          title: p.fullName,
          subtitle: [p.patientCode, p.phoneE164, p.age != null ? `${p.age} tuổi` : null]
            .filter(Boolean)
            .join(" · "),
          to: `/patients/${p.id}`,
        })),
      },
      {
        key: "appointments",
        label: "Lịch hẹn",
        loading: appointments.isLoading,
        hits: (appointments.data?.content ?? []).map((a) => ({
          id: a.id,
          title: a.patientName,
          subtitle: [
            new Date(a.startAt).toLocaleString("vi-VN", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }),
            a.doctorName,
            a.serviceName,
            // Mã đặt lịch (#19396): khi lễ tân vừa gõ mã bệnh nhân đọc, dòng này là chỗ họ đối
            // chiếu thấy đúng lịch hẹn đó — không phải mở chi tiết ra mới biết.
            a.bookingCode,
          ]
            .filter(Boolean)
            .join(" · "),
          // Straight to the appointment record — /appointments?focus= would be
          // another dead link, which is the very bug this component fixes.
          to: `/appointments/${a.id}`,
        })),
      },
    ],
    [patients.data, patients.isLoading, appointments.data, appointments.isLoading],
  );
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const term = useDebounce(q.trim(), 200);
  const enabled = term.length >= MIN_CHARS;
  const groups = useSources(term, enabled && open);

  // One flat list drives arrow-key movement across every group.
  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups]);
  const loading = groups.some((g) => g.loading);
  const empty = enabled && !loading && flat.length === 0;

  useEffect(() => setCursor(0), [term]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // Ctrl/Cmd+K focuses the box, as before.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  function go(hit: Hit) {
    setOpen(false);
    setQ("");
    navigate(hit.to);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!flat.length) return;
      setCursor((c) => (e.key === "ArrowDown" ? (c + 1) % flat.length : (c - 1 + flat.length) % flat.length));
      setOpen(true);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      // Enter with results picks the highlighted one. With none — because the
      // term is too short, or nothing matched — fall back to the full patient
      // list carrying the query, so Enter is never a dead end.
      if (flat.length) go(flat[cursor] ?? flat[0]);
      else if (q.trim()) {
        setOpen(false);
        navigate(`/patients?q=${encodeURIComponent(q.trim())}`);
      }
    }
  }

  let running = -1;

  return (
    <div className="relative w-full max-w-md" ref={boxRef}>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        // #19396: ô này nay tra được cả MÃ ĐẶT LỊCH bệnh nhân đọc từ Zalo Mini App. QA không thử
        // gõ mã vào đây vì dòng chữ cũ không khai nhận nó — một khả năng không được khai thì
        // không ai dùng, nên câu chữ là một nửa của việc sửa.
        placeholder="Tìm bệnh nhân, lịch hẹn (Tên, SĐT, mã BN, mã đặt lịch)…"
        aria-label="Tìm kiếm"
        role="combobox"
        aria-expanded={open}
        aria-controls="gsearch-results"
        autoComplete="off"
        className="pl-9 pr-16"
      />
      <span className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-input bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-block">
        Ctrl + K
      </span>

      {open && q.trim().length > 0 && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-md border bg-card py-1 shadow-md"
          id="gsearch-results"
          role="listbox"
        >
          {q.trim().length < MIN_CHARS && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Nhập ít nhất {MIN_CHARS} ký tự…
            </div>
          )}

          {enabled && loading && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Đang tìm…</div>
          )}

          {empty && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Không tìm thấy kết quả nào.
            </div>
          )}

          {enabled &&
            groups.map((g) =>
              g.hits.length === 0 ? null : (
                <div key={g.key}>
                  <div className="px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {g.label}
                  </div>
                  {g.hits.map((hit) => {
                    running += 1;
                    const active = running === cursor;
                    return (
                      <button
                        key={`${g.key}-${hit.id}`}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={cn(
                          "flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-muted",
                          active && "bg-muted",
                        )}
                        onMouseEnter={() => setCursor(flat.findIndex((h) => h.id === hit.id))}
                        onClick={() => go(hit)}
                      >
                        <span className="font-medium text-foreground">{hit.title}</span>
                        {hit.subtitle && (
                          <span className="text-xs text-muted-foreground">{hit.subtitle}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ),
            )}

          {enabled && flat.length > 0 && (
            <button
              type="button"
              className="mt-1 block w-full border-t px-3 py-2 text-left text-sm text-primary hover:bg-muted hover:underline"
              onClick={() => {
                setOpen(false);
                navigate(`/patients?q=${encodeURIComponent(q.trim())}`);
              }}
            >
              Xem tất cả bệnh nhân khớp “{q.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
